import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Gift, Award, Star, Zap, TrendingUp, Trophy, Target,
  ChevronRight, Lock, Loader2
} from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';

export default function DoadorPontosPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [badgesDisponiveis, setBadgesDisponiveis] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pontosData, badgesData] = await Promise.all([
        doadorApiCall('/doador/pontos'),
        doadorApiCall('/doador/badges-disponiveis')
      ]);
      setData(pontosData);
      setBadgesDisponiveis(badgesData.badges || {});
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  const { pontos, badges, proximo_nivel, tabela_pontos } = data || {};
  const badgesConquistados = badges?.map(b => b.id) || [];

  return (
    <div className="space-y-6" data-testid="doador-pontos-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Gift className="w-7 h-7 text-amber-500" />
          Pontos & Badges
        </h1>
        <p className="text-slate-500 mt-1">Acompanhe o seu progresso e conquiste novas recompensas</p>
      </div>

      {/* Pontos Card */}
      <Card className="bg-gradient-to-r from-amber-500 to-rose-500 text-white border-0 overflow-hidden">
        <CardContent className="pt-6 relative">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-amber-100 font-medium">Seus Pontos</p>
              <p className="text-5xl font-bold mt-1">{pontos?.toLocaleString('pt-PT') || 0}</p>
            </div>
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <Star className="w-10 h-10" />
            </div>
          </div>

          {proximo_nivel && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-amber-100">Próximo: {proximo_nivel.nome}</span>
                <span className="font-medium">{pontos}/{proximo_nivel.pontos_necessarios}</span>
              </div>
              <Progress 
                value={(pontos / proximo_nivel.pontos_necessarios) * 100} 
                className="h-3 bg-white/20"
              />
              <p className="text-amber-100 text-sm mt-2">
                Faltam {proximo_nivel.pontos_faltam} pontos para o próximo nível
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Badges Conquistados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Badges Conquistados
            </CardTitle>
            <CardDescription>
              {badges?.length || 0} de {Object.keys(badgesDisponiveis).length} badges
            </CardDescription>
          </CardHeader>
          <CardContent>
            {badges && badges.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {badges.map((badge, idx) => (
                  <div 
                    key={idx}
                    className="p-4 bg-gradient-to-br from-amber-50 to-rose-50 rounded-xl border border-amber-200 text-center"
                  >
                    <span className="text-3xl">{badge.icone}</span>
                    <p className="font-semibold text-slate-800 mt-2">{badge.nome}</p>
                    <p className="text-xs text-slate-500 mt-1">{badge.descricao}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">Continue a doar para conquistar badges!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Badges por Conquistar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-slate-400" />
              Por Conquistar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(badgesDisponiveis)
                .filter(([id]) => !badgesConquistados.includes(id))
                .map(([id, badge]) => (
                  <div 
                    key={id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                        <Lock className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">{badge.nome}</p>
                        <p className="text-xs text-slate-500">{badge.descricao}</p>
                      </div>
                    </div>
                    {badge.pontos_necessarios && (
                      <Badge variant="outline" className="text-xs">
                        {badge.pontos_necessarios} pts
                      </Badge>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Como Ganhar Pontos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Como Ganhar Pontos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tabela_pontos && Object.entries(tabela_pontos).map(([acao, pts]) => {
              const labels = {
                'registro': 'Criar conta',
                'primeira_doacao': 'Primeira doação',
                'doacao_produto': 'Doar produtos',
                'doacao_financeira': 'Doação financeira',
                'doacao_voluntariado': 'Voluntariado',
                'doacao_equipamento': 'Doar equipamentos',
                'referencia_amigo': 'Referir amigo',
                'campanha_participada': 'Participar campanha',
                'perfil_completo': 'Completar perfil',
                'newsletter_ativo': 'Subscrever newsletter'
              };
              return (
                <div key={acao} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-700">{labels[acao] || acao}</span>
                  <Badge className="bg-amber-100 text-amber-700">+{pts}</Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
